/**
 * Kadmin Routes
 *
 * Route shape:
 *   { routeRole, type, name, key, icon, route, component, action, subject }
 *
 * type: "collapse" = visible in sidenav
 *       "hidden"   = auth-protected but not in sidenav
 *       "public"   = no auth required
 */

import { useContext } from "react";
import { Navigate } from "react-router-dom";
import Icon from "@mui/material/Icon";

import { AuthContext } from "context/AuthContext";
import { useAbility, ACTIONS, SUBJECTS } from "context/AbilityContext";

// Layouts (lazy placeholders — will be replaced with real pages)
import Dashboard from "layouts/dashboard";
import Samples from "layouts/samples";
import Users from "layouts/users";
import Roles from "layouts/roles";
import RoleDetailEditor from "layouts/roles/RoleDetailEditor";
import UserDetailEditor from "layouts/users/UserDetailEditor";
import InviteCodes from "layouts/invite-codes";
import Notifications from "layouts/notifications";
import NotificationTemplates from "layouts/notifications/NotificationTemplates";
import Settings from "layouts/settings";
import Portals from "layouts/portals/admin/Portals";
import PortalEditor from "layouts/portals/admin/PortalEditor";
import PortalPageEditor from "layouts/portals/admin/PortalPageEditor";


// ---------------------------------------------------------------------------
// Route guard components
// ---------------------------------------------------------------------------

/** Role-based guard — waits for auth, then checks role */
const RoleGuard = ({ role: requiredRole, children }) => {
  const { activeRole, authLoading } = useContext(AuthContext);
  if (authLoading) return null;
  return activeRole === requiredRole ? children : <Navigate to="/login" />;
};

/** CASL-based guard — checks ability for action+subject */
const CaslGuard = ({ action, subject, redirectTo = "/login", children }) => {
  const { activeRole, authLoading } = useContext(AuthContext);
  const ability = useAbility();
  if (authLoading) return null;
  if (!activeRole) return <Navigate to="/login" />;
  if (action && subject && !ability.can(action, subject)) {
    return <Navigate to={redirectTo} />;
  }
  return children;
};

export { RoleGuard, CaslGuard };

// ---------------------------------------------------------------------------
// Admin routes
// ---------------------------------------------------------------------------
const routes = [
  // ── Admin ──────────────────────────────────────────────────────────
  {
    routeRole: "admin",
    type: "collapse",
    name: "Dashboard",
    nameKey: "nav.dashboard",
    key: "admin/dashboard",
    module: "dashboard",
    icon: <Icon>dashboard</Icon>,
    route: "/admin/dashboard",
    component: (
      <RoleGuard role="admin">
        <Dashboard />
      </RoleGuard>
    ),
  },
  {
    routeRole: "admin",
    type: "collapse",
    name: "Users",
    nameKey: "nav.users",
    key: "admin/users",
    module: "users",
    icon: <Icon>people</Icon>,
    route: "/admin/users",
    component: (
      <CaslGuard action={ACTIONS.READ} subject={SUBJECTS.USER}>
        <Users />
      </CaslGuard>
    ),
  },
  {
    routeRole: "admin",
    type: "collapse",
    name: "Roles",
    nameKey: "nav.roles",
    key: "admin/roles",
    module: "roles",
    icon: <Icon>admin_panel_settings</Icon>,
    route: "/admin/roles",
    component: (
      <CaslGuard action={ACTIONS.READ} subject={SUBJECTS.ROLE}>
        <Roles />
      </CaslGuard>
    ),
  },
  {
    routeRole: "admin",
    type: "collapse",
    name: "Samples",
    nameKey: "nav.samples",
    key: "admin/samples",
    module: "samples",
    icon: <Icon>science</Icon>,
    route: "/admin/samples",
    component: (
      <CaslGuard action={ACTIONS.READ} subject={SUBJECTS.SAMPLE}>
        <Samples />
      </CaslGuard>
    ),
  },
  {
    routeRole: "admin",
    type: "collapse",
    name: "Invite Codes",
    nameKey: "nav.invites",
    key: "admin/invite-codes",
    module: "invites",
    icon: <Icon>mail</Icon>,
    route: "/admin/invite-codes",
    component: (
      <CaslGuard action={ACTIONS.READ} subject={SUBJECTS.INVITE_CODE}>
        <InviteCodes />
      </CaslGuard>
    ),
  },
  {
    routeRole: "admin",
    type: "collapse",
    name: "Notifications",
    nameKey: "nav.notifications",
    key: "admin/notifications",
    module: "notifications",
    icon: <Icon>notifications</Icon>,
    route: "/admin/notifications",
    component: (
      <CaslGuard action={ACTIONS.READ} subject={SUBJECTS.ADMIN_NOTIFICATION}>
        <Notifications />
      </CaslGuard>
    ),
  },
  {
    routeRole: "admin",
    type: "collapse",
    name: "Settings",
    nameKey: "nav.settings",
    key: "admin/settings",
    module: "settings",
    icon: <Icon>settings</Icon>,
    route: "/admin/settings",
    component: (
      <RoleGuard role="admin">
        <Settings />
      </RoleGuard>
    ),
  },
  {
    routeRole: "admin",
    type: "collapse",
    name: "Portals",
    nameKey: "nav.portals",
    key: "admin/portals",
    module: "portals",
    icon: <Icon>web</Icon>,
    route: "/admin/portals",
    component: (
      <CaslGuard action={ACTIONS.READ} subject={SUBJECTS.PORTAL}>
        <Portals />
      </CaslGuard>
    ),
  },

  // ── User role ──────────────────────────────────────────────────────
  {
    routeRole: "user",
    type: "collapse",
    name: "Dashboard",
    nameKey: "nav.dashboard",
    key: "user/dashboard",
    module: "dashboard",
    icon: <Icon>dashboard</Icon>,
    route: "/user/dashboard",
    component: (
      <RoleGuard role="user">
        <Dashboard />
      </RoleGuard>
    ),
  },
  {
    routeRole: "user",
    type: "collapse",
    name: "Samples",
    nameKey: "nav.samples",
    key: "user/samples",
    module: "samples",
    icon: <Icon>science</Icon>,
    route: "/user/samples",
    component: (
      <RoleGuard role="user">
        <Samples />
      </RoleGuard>
    ),
  },
];

// Hidden routes (not in sidenav)
const authRoutes = [
  {
    routeRole: "admin",
    type: "hidden",
    name: "User Detail",
    key: "admin/users/:id",
    route: "/admin/users/:id",
    component: (
      <CaslGuard action={ACTIONS.READ} subject={SUBJECTS.USER}>
        <UserDetailEditor />
      </CaslGuard>
    ),
  },
  {
    routeRole: "admin",
    type: "hidden",
    name: "Role Detail",
    key: "admin/roles/:id",
    route: "/admin/roles/:id",
    component: (
      <CaslGuard action={ACTIONS.READ} subject={SUBJECTS.ROLE}>
        <RoleDetailEditor />
      </CaslGuard>
    ),
  },
  {
    routeRole: "admin",
    type: "hidden",
    name: "Sample Detail",
    key: "admin/samples/:id",
    route: "/admin/samples/:id",
    component: (
      <CaslGuard action={ACTIONS.READ} subject={SUBJECTS.SAMPLE}>
        <Samples />
      </CaslGuard>
    ),
  },
  // Notification Templates routes
  {
    routeRole: "admin",
    type: "hidden",
    name: "Notification Templates",
    key: "admin/notification-templates",
    route: "/admin/notification-templates",
    component: (
      <CaslGuard action={ACTIONS.READ} subject={SUBJECTS.NOTIFICATION_TEMPLATE}>
        <NotificationTemplates />
      </CaslGuard>
    ),
  },
  {
    routeRole: "admin",
    type: "hidden",
    name: "Notification Template Editor",
    key: "admin/notification-templates/:id",
    route: "/admin/notification-templates/:id",
    component: (
      <CaslGuard action={ACTIONS.READ} subject={SUBJECTS.NOTIFICATION_TEMPLATE}>
        <NotificationTemplates />
      </CaslGuard>
    ),
  },
  {
    routeRole: "admin",
    type: "hidden",
    name: "Notification Template Edit",
    key: "admin/notification-templates/:id/edit",
    route: "/admin/notification-templates/:id/edit",
    component: (
      <CaslGuard action={ACTIONS.UPDATE} subject={SUBJECTS.NOTIFICATION_TEMPLATE}>
        <NotificationTemplates />
      </CaslGuard>
    ),
  },
  // Portal admin routes
  {
    routeRole: "admin",
    type: "hidden",
    name: "New Portal",
    key: "admin/portals/new",
    route: "/admin/portals/new",
    component: (
      <CaslGuard action={ACTIONS.CREATE} subject={SUBJECTS.PORTAL}>
        <PortalEditor />
      </CaslGuard>
    ),
  },
  {
    routeRole: "admin",
    type: "hidden",
    name: "Portal Detail",
    key: "admin/portals/:id",
    route: "/admin/portals/:id",
    component: (
      <CaslGuard action={ACTIONS.READ} subject={SUBJECTS.PORTAL}>
        <PortalEditor />
      </CaslGuard>
    ),
  },
  {
    routeRole: "admin",
    type: "hidden",
    name: "Portal Page Editor",
    key: "admin/portals/:id/pages/:pageId",
    route: "/admin/portals/:id/pages/:pageId",
    component: (
      <CaslGuard action={ACTIONS.UPDATE} subject={SUBJECTS.PORTAL_PAGE}>
        <PortalPageEditor />
      </CaslGuard>
    ),
  },
];

export default routes;
export { authRoutes };
